import { Component, computed, inject, input, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TextareaModule } from 'primeng/textarea';
import { FormBuilderEditCultureService } from './form-builder-edit-culture.service';
import { LocalizationService } from '../../../core/localization/localization.service';
import { AinFormFieldComponent } from '../../../shared/design-system/form-field.component';
import { InfoHintComponent } from '../../../shared/design-system/info-hint.component';
import {
  FORM_EVENT_ACTION_TYPES,
  FORM_EVENT_KEYS,
  FormEventAction,
  FormEventActionParams,
  FormEventActionType,
  FormEventKey,
  FormEvents,
  FormSchemaModel,
  FormScript,
  FormWebhook,
  fieldLabelOptions,
  readEventActionParams,
  readLocalizedText,
  writeLocalizedText
} from '../../../shared/dynamic-form/form-schema.models';

@Component({
  standalone: true,
  selector: 'ain-form-builder-events-panel',
  imports: [
    FormsModule,
    ButtonModule,
    InputTextModule,
    SelectModule,
    TextareaModule,
    AinFormFieldComponent,
    InfoHintComponent
  ],
  template: `
    <section class="events-panel">
      <ain-info-hint
        [titleKey]="'dynamic.builder.events.infoTitle'"
        [descriptionKey]="'dynamic.builder.events.infoDescription'"
        [bulletKeys]="['dynamic.builder.events.infoBullet']" />

      <p-select
        [options]="eventKeyOptions()"
        optionLabel="label"
        optionValue="value"
        [ngModel]="activeEventKey()"
        appendTo="body"
        (ngModelChange)="activeEventKey.set($event)" />

      <div class="toolbar">
        <strong>{{ eventTitle() }}</strong>
        @if (canEdit()) {
          <button pButton type="button" text icon="pi pi-plus" [label]="t('dynamic.builder.events.addAction')" (click)="addAction()"></button>
        }
      </div>

      @for (action of activeActions(); track $index; let idx = $index) {
        <article class="action-card">
          <div class="action-header">
            <span>{{ t('dynamic.builder.events.actionOrder') }} {{ idx + 1 }}</span>
            @if (canEdit()) {
              <div class="action-buttons">
                <button pButton type="button" text icon="pi pi-arrow-up" [disabled]="idx === 0" (click)="moveAction(idx, -1)"></button>
                <button pButton type="button" text icon="pi pi-arrow-down" [disabled]="idx === activeActions().length - 1" (click)="moveAction(idx, 1)"></button>
                <button pButton type="button" severity="danger" text icon="pi pi-trash" (click)="removeAction(idx)"></button>
              </div>
            }
          </div>

          <ain-form-field [label]="t('dynamic.builder.events.actionType')">
            <p-select
              [options]="actionTypeOptions()"
              optionLabel="label"
              optionValue="value"
              [ngModel]="action.action"
              [disabled]="!canEdit()"
              appendTo="body"
              (ngModelChange)="updateActionType(idx, $event)" />
          </ain-form-field>

          @switch (action.action) {
            @case ('setValue') {
              <ain-form-field [label]="t('dynamic.builder.events.targetField')">
                <p-select
                  [options]="fieldOptions()"
                  optionLabel="label"
                  optionValue="value"
                  [ngModel]="readParams(action).targetField ?? ''"
                  [disabled]="!canEdit()"
                  [filter]="true"
                  appendTo="body"
                  (ngModelChange)="updateParams(idx, { targetField: $event })" />
              </ain-form-field>
              <ain-form-field [label]="t('dynamic.builder.events.staticValue')">
                <input
                  pInputText
                  [ngModel]="readParams(action).value ?? ''"
                  [disabled]="!canEdit()"
                  (ngModelChange)="updateParams(idx, { value: $event })" />
              </ain-form-field>
              <ain-form-field [label]="t('dynamic.builder.events.expression')">
                <input
                  pInputText
                  [ngModel]="readParams(action).expression ?? ''"
                  [disabled]="!canEdit()"
                  (ngModelChange)="updateParams(idx, { expression: $event })" />
              </ain-form-field>
            }
            @case ('showMessage') {
              <ain-form-field [label]="t('dynamic.builder.events.message')">
                <textarea
                  pTextarea
                  rows="2"
                  [ngModel]="messageValue(action)"
                  [disabled]="!canEdit()"
                  (ngModelChange)="updateMessage(idx, $event)"></textarea>
              </ain-form-field>
            }
            @case ('callWebhook') {
              <ain-form-field [label]="t('dynamic.builder.events.webhook')">
                <p-select
                  [options]="webhookOptions()"
                  optionLabel="label"
                  optionValue="value"
                  [ngModel]="action.webhookRef ?? readParams(action).webhookRef ?? ''"
                  [disabled]="!canEdit()"
                  appendTo="body"
                  (ngModelChange)="updateWebhookRef(idx, $event)" />
              </ain-form-field>
            }
            @case ('redirect') {
              <ain-form-field [label]="t('dynamic.builder.events.url')">
                <input
                  pInputText
                  [ngModel]="action.url ?? ''"
                  [disabled]="!canEdit()"
                  (ngModelChange)="updateUrl(idx, $event)" />
              </ain-form-field>
            }
            @case ('runScript') {
              <ain-form-field [label]="t('dynamic.builder.events.script')">
                <p-select
                  [options]="scriptOptions()"
                  optionLabel="label"
                  optionValue="value"
                  [ngModel]="action.scriptRef ?? ''"
                  [disabled]="!canEdit()"
                  appendTo="body"
                  (ngModelChange)="updateScriptRef(idx, $event)" />
              </ain-form-field>
            }
          }
        </article>
      } @empty {
        <p class="empty">{{ t('dynamic.builder.events.empty') }}</p>
      }
    </section>
  `,
  styles: [`
    .events-panel { display: grid; gap: 12px; }
    .toolbar { display: flex; justify-content: space-between; align-items: center; gap: 8px; flex-wrap: wrap; }
    .action-card {
      border: 1px solid var(--surface-border);
      border-radius: 8px;
      padding: 12px;
      display: grid;
      gap: 10px;
    }
    .action-header { display: flex; justify-content: space-between; align-items: center; gap: 8px; }
    .action-buttons { display: flex; gap: 4px; }
    .empty { margin: 0; color: var(--text-color-secondary); }
  `]
})
export class FormBuilderEventsPanelComponent {
  private readonly localization = inject(LocalizationService);
  private readonly editCultureService = inject(FormBuilderEditCultureService);

  readonly schema = input.required<FormSchemaModel>();
  readonly culture = input.required<string>();
  readonly scripts = input<FormScript[]>([]);
  readonly webhooks = input<FormWebhook[]>([]);
  readonly canEdit = input(true);

  readonly eventsChange = output<FormEvents>();

  readonly activeEventKey = signal<FormEventKey>('onLoad');

  readonly eventKeyOptions = computed(() =>
    FORM_EVENT_KEYS.map(key => ({
      label: this.t(`dynamic.builder.events.key.${key}`),
      value: key
    }))
  );

  readonly actionTypeOptions = computed(() =>
    FORM_EVENT_ACTION_TYPES.map(value => ({
      label: this.t(`dynamic.builder.events.action.${value}`),
      value
    }))
  );

  readonly fieldOptions = computed(() => fieldLabelOptions(this.schema(), this.culture()));

  readonly scriptOptions = computed(() =>
    (this.scripts() ?? []).map(script => ({
      label: script.ref,
      value: script.ref
    }))
  );

  readonly webhookOptions = computed(() =>
    (this.webhooks() ?? []).map(webhook => ({
      label: webhook.name?.trim() ? `${webhook.name} (${webhook.ref})` : webhook.ref,
      value: webhook.ref
    }))
  );

  readonly activeActions = computed(() => {
    const events = this.schema().events ?? {};
    return [...(events[this.activeEventKey()] ?? [])];
  });

  readonly eventTitle = computed(() => this.t(`dynamic.builder.events.key.${this.activeEventKey()}`));

  t(key: string): string {
    return this.localization.translate(key);
  }

  readParams(action: FormEventAction): FormEventActionParams {
    return readEventActionParams(action);
  }

  messageValue(action: FormEventAction): string {
    const params = readEventActionParams(action);
    return readLocalizedText(params.message, this.editCultureService.editCulture());
  }

  addAction(): void {
    this.patchActiveActions([...this.activeActions(), { action: 'recalculate' }]);
  }

  removeAction(index: number): void {
    const next = this.activeActions().filter((_, idx) => idx !== index);
    this.patchActiveActions(next);
  }

  moveAction(index: number, delta: number): void {
    const next = [...this.activeActions()];
    const target = index + delta;
    if (target < 0 || target >= next.length) {
      return;
    }
    [next[index], next[target]] = [next[target], next[index]];
    this.patchActiveActions(next);
  }

  updateActionType(index: number, actionType: FormEventActionType): void {
    const next = [...this.activeActions()];
    next[index] = { action: actionType };
    this.patchActiveActions(next);
  }

  updateParams(index: number, patch: FormEventActionParams): void {
    const next = [...this.activeActions()];
    const current = readEventActionParams(next[index]);
    next[index] = {
      ...next[index],
      params: { ...current, ...patch }
    };
    this.patchActiveActions(next);
  }

  updateMessage(index: number, value: string): void {
    const next = [...this.activeActions()];
    const current = readEventActionParams(next[index]);
    next[index] = {
      ...next[index],
      params: {
        ...current,
        message: writeLocalizedText(current.message, this.editCultureService.editCulture(), value)
      }
    };
    this.patchActiveActions(next);
  }

  updateWebhookRef(index: number, webhookRef: string): void {
    const next = [...this.activeActions()];
    next[index] = { ...next[index], action: 'callWebhook', webhookRef };
    this.patchActiveActions(next);
  }

  updateUrl(index: number, url: string): void {
    const next = [...this.activeActions()];
    next[index] = { ...next[index], action: 'redirect', url };
    this.patchActiveActions(next);
  }

  updateScriptRef(index: number, scriptRef: string): void {
    const next = [...this.activeActions()];
    next[index] = { ...next[index], action: 'runScript', scriptRef };
    this.patchActiveActions(next);
  }

  private patchActiveActions(actions: FormEventAction[]): void {
    const key = this.activeEventKey();
    const events: FormEvents = { ...(this.schema().events ?? {}) };
    events[key] = actions;
    this.eventsChange.emit(events);
  }
}
